import React from "react";
import { createPortal } from "react-dom";

const IndentMaterialSelectionDrawer = ({
  open,
  isVisible,
  reviewState,
  setReviewState,
  visibleMaterialIndexes,
  visibleMaterialsSelected,
  onClose,
  onAdd,
  onToggleMaterial,
  onToggleAll,
}) => {
  const isPendingPr = reviewState.source === "pendingPr";
  const isMaterialMaster = reviewState.source === "materialMaster";
  const isLabourIndent = reviewState.itemType === "labour";
  const itemLabel = isLabourIndent ? "labour items" : "materials";
  const itemColumnLabel = isLabourIndent ? "Work Description" : "Material";
  const indentOptions = React.useMemo(() => {
    if (isPendingPr) return [];

    return [
      ...new Set(
        reviewState.materials
          .map((material) => String(material?.sourceIndentId || material?.indentNumber || "").trim())
          .filter(Boolean)
      ),
    ];
  }, [isPendingPr, reviewState.materials]);
  const hasMultipleIndentTabs = !isPendingPr && indentOptions.length > 1;

  const pendingPrFilterOptions = React.useMemo(() => {
    if (!isPendingPr) {
      return {
        projects: [],
        categories: [],
      };
    }

    const getUniqueSortedValues = (key) => [
      ...new Set(
        reviewState.materials
          .map((material) => String(material?.[key] || "").trim())
          .filter(Boolean)
      ),
    ].sort((firstValue, secondValue) =>
      firstValue.localeCompare(secondValue, undefined, { sensitivity: "base" })
    );

    return {
      projects: getUniqueSortedValues("projectName"),
      categories: getUniqueSortedValues("category"),
    };
  }, [isPendingPr, reviewState.materials]);

  const addPendingPrFilterValue = (filterKey, value) => {
    const filterValue = String(value || "").trim();
    if (!filterValue) return;

    setReviewState((previousState) => {
      const selectedValues = Array.isArray(previousState[filterKey])
        ? previousState[filterKey]
        : [];

      if (selectedValues.includes(filterValue)) return previousState;

      return {
        ...previousState,
        [filterKey]: [...selectedValues, filterValue],
      };
    });
  };

  const selectPendingPrFilterValue = ({ filterKey, value, searchKey, openKey }) => {
    addPendingPrFilterValue(filterKey, value);
    setReviewState((previousState) => ({
      ...previousState,
      [searchKey]: "",
      [openKey]: false,
    }));
  };

  const removePendingPrFilterValue = (filterKey, value) => {
    setReviewState((previousState) => ({
      ...previousState,
      [filterKey]: Array.isArray(previousState[filterKey])
        ? previousState[filterKey].filter((selectedValue) => selectedValue !== value)
        : [],
    }));
  };

  const clearPendingPrFilters = () => {
    setReviewState((previousState) => ({
      ...previousState,
      projectFilters: [],
      categoryFilters: [],
      projectFilterSearch: "",
      categoryFilterSearch: "",
      projectFilterOpen: false,
      categoryFilterOpen: false,
    }));
  };

  const filterPendingPrOptions = (options = [], searchTerm = "") => {
    const normalizedSearchTerm = String(searchTerm || "").trim().toLowerCase();

    if (!normalizedSearchTerm) return options;

    return options.filter((option) =>
      String(option || "").toLowerCase().includes(normalizedSearchTerm)
    );
  };

  const renderSearchablePendingPrFilter = ({
    label,
    options,
    selectedValues,
    searchKey,
    openKey,
    filterKey,
    placeholder,
  }) => {
    const searchValue = reviewState[searchKey] || "";
    const isOpen = Boolean(reviewState[openKey]);
    const availableOptions = filterPendingPrOptions(
      options.filter((option) => !selectedValues.includes(option)),
      searchValue
    );

    return (
      <div className="relative">
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
          {label}
        </label>
        <input
          type="text"
          value={searchValue}
          onFocus={() =>
            setReviewState((previousState) => ({
              ...previousState,
              [openKey]: true,
            }))
          }
          onBlur={() =>
            setTimeout(() => {
              setReviewState((previousState) => ({
                ...previousState,
                [openKey]: false,
              }));
            }, 150)
          }
          onChange={(event) =>
            setReviewState((previousState) => ({
              ...previousState,
              [searchKey]: event.target.value,
              [openKey]: true,
            }))
          }
          placeholder={placeholder}
          className="h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-300"
        />
        {isOpen && (
          <div className="absolute left-0 right-0 top-[62px] z-20 max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
            {availableOptions.map((option) => (
              <button
                key={option}
                type="button"
                onMouseDown={(event) => {
                  event.preventDefault();
                  selectPendingPrFilterValue({
                    filterKey,
                    value: option,
                    searchKey,
                    openKey,
                  });
                }}
                className="block w-full truncate px-3 py-2 text-left text-sm text-gray-700 hover:bg-orange-50"
                title={option}
              >
                {option}
              </button>
            ))}
            {availableOptions.length === 0 && (
              <p className="px-3 py-3 text-center text-xs text-gray-400">
                No {label.toLowerCase()} found.
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-y-0 left-[85px] right-0 z-[1200] bg-black bg-opacity-30 flex justify-end overflow-hidden">
      <div
        className={`flex h-full w-full flex-col bg-white shadow-2xl transition-transform duration-500 ease-out ${
          isVisible ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between border-b bg-[#FFF8F2] px-5 py-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-[#1F1E29]">
              {isPendingPr
                ? "Select Materials from Pending PR"
                : isMaterialMaster
                  ? "Select Materials from Master List"
                  : isLabourIndent
                  ? "Select Labour Items from Indent"
                  : "Select Materials from Indent"}
            </h3>
            <span className="text-xs text-gray-500">
              Choose the {itemLabel} you want to add to this RFQ.
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-full flex items-center justify-center text-gray-500 hover:bg-white hover:text-gray-800"
          >
            x
          </button>
        </div>

        {isPendingPr && (
          <div className="border-b bg-[#FAFAF9] px-6 py-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_1fr_auto]">
              {renderSearchablePendingPrFilter({
                label: "Project",
                options: pendingPrFilterOptions.projects,
                selectedValues: reviewState.projectFilters || [],
                searchKey: "projectFilterSearch",
                openKey: "projectFilterOpen",
                filterKey: "projectFilters",
                placeholder: "Search and select projects",
              })}
              {renderSearchablePendingPrFilter({
                label: "Category",
                options: pendingPrFilterOptions.categories,
                selectedValues: reviewState.categoryFilters || [],
                searchKey: "categoryFilterSearch",
                openKey: "categoryFilterOpen",
                filterKey: "categoryFilters",
                placeholder: "Search and select categories",
              })}
              <button
                type="button"
                onClick={clearPendingPrFilters}
                className="mt-5 h-9 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-600 hover:bg-gray-50"
              >
                Clear all filters
              </button>
            </div>

            {(reviewState.projectFilters?.length > 0 ||
              reviewState.categoryFilters?.length > 0) && (
              <div className="mt-3 flex flex-wrap gap-2">
                {(reviewState.projectFilters || []).map((projectName) => (
                  <span
                    key={`project-${projectName}`}
                    className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700"
                  >
                    Project: {projectName}
                    <button
                      type="button"
                      onClick={() => removePendingPrFilterValue("projectFilters", projectName)}
                      className="text-blue-500 hover:text-blue-900"
                    >
                      x
                    </button>
                  </span>
                ))}
                {(reviewState.categoryFilters || []).map((categoryName) => (
                  <span
                    key={`category-${categoryName}`}
                    className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-medium text-orange-700"
                  >
                    Category: {categoryName}
                    <button
                      type="button"
                      onClick={() => removePendingPrFilterValue("categoryFilters", categoryName)}
                      className="text-orange-500 hover:text-orange-900"
                    >
                      x
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {hasMultipleIndentTabs && (
          <div className="flex items-center gap-2 overflow-x-auto border-b bg-white px-6 py-2">
            <button
              type="button"
              onClick={() =>
                setReviewState((previousState) => ({
                  ...previousState,
                  activeIndentFilter: "all",
                }))
              }
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                (reviewState.activeIndentFilter || "all") === "all"
                  ? "bg-[#1E1E08] text-white"
                  : "border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              All {itemLabel} ({reviewState.materials.length})
            </button>
            {indentOptions.map((indentId) => {
              const count = reviewState.materials.filter(
                (material) => String(material?.sourceIndentId || material?.indentNumber || "") === indentId
              ).length;

              return (
                <button
                  key={indentId}
                  type="button"
                  onClick={() =>
                    setReviewState((previousState) => ({
                      ...previousState,
                      activeIndentFilter: indentId,
                    }))
                  }
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                    reviewState.activeIndentFilter === indentId
                      ? "bg-orange-600 text-white"
                      : "border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100"
                  }`}
                >
                  Indent {indentId} ({count})
                </button>
              );
            })}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-white px-6 py-3">
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <input
              type="checkbox"
              checked={visibleMaterialsSelected}
              onChange={onToggleAll}
              className="h-4 w-4"
            />
            Select all visible {itemLabel}
          </label>
          <div className="flex min-w-[260px] flex-1 justify-center">
            <input
              type="text"
              value={reviewState.searchTerm || ""}
              onChange={(event) =>
                setReviewState((previousState) => ({
                  ...previousState,
                  searchTerm: event.target.value,
                }))
              }
              placeholder={
                isPendingPr
                  ? "Search material, project, or category..."
                  : isMaterialMaster
                    ? "Search material, code, category, brand, or specification..."
                    : `Search ${isLabourIndent ? "work description" : "material"}, indent, or project...`
              }
              className="h-9 w-full max-w-xl rounded-md border border-gray-300 px-3 text-sm focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-300"
            />
          </div>
          <p className="text-xs text-gray-500">
            {reviewState.selectedIndexes.length} of {reviewState.materials.length} selected
          </p>
        </div>

        <div className="flex-1 overflow-auto">
          <table className={`w-full text-sm ${isLabourIndent ? "min-w-[820px]" : isMaterialMaster ? "min-w-[1180px]" : "min-w-[1080px]"}`}>
            <thead className="sticky top-0 bg-[#F5F5F3] text-xs text-[#5F5F55]">
              <tr>
                <th className="text-left px-4 py-3 w-12">Pick</th>
                {isPendingPr && (
                  <>
                    <th className="text-left px-4 py-3">Project</th>
                    <th className="text-left px-4 py-3">Category</th>
                  </>
                )}
                {hasMultipleIndentTabs && <th className="text-left px-4 py-3">Indent</th>}
                {!isPendingPr && !isLabourIndent && <th className="text-left px-4 py-3">Code</th>}
                {isMaterialMaster && <th className="text-left px-4 py-3">Material SubType</th>}
                <th className="text-left px-4 py-3">{itemColumnLabel}</th>
                <th className="text-left px-4 py-3">Qty</th>
                <th className="text-left px-4 py-3">Unit</th>
                {!isPendingPr && !isLabourIndent && <th className="text-left px-4 py-3">Brands</th>}
                {isPendingPr && <th className="text-left px-4 py-3">Date</th>}
              </tr>
            </thead>
            <tbody>
              {visibleMaterialIndexes.map((index) => {
                const material = reviewState.materials[index];
                const isSelected = reviewState.selectedIndexes.includes(index);
                const itemName = isLabourIndent ? material.skill : material.name;

                return (
                  <tr
                    key={`${material.code || material.itemNumber || ""}-${itemName}-${index}`}
                    className={`${isSelected ? "bg-orange-50" : "bg-white"} border-t`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleMaterial(index)}
                        className="h-4 w-4"
                      />
                    </td>
                    {isPendingPr && (
                      <>
                        <td className="px-4 py-3 text-xs text-gray-700">
                          {material.projectName || "-"}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-700">
                          {material.category || "-"}
                        </td>
                      </>
                    )}
                    {hasMultipleIndentTabs && (
                      <td className="px-4 py-3 text-xs font-semibold text-orange-700">
                        {material.sourceIndentId || material.indentNumber || "-"}
                      </td>
                    )}
                    {!isPendingPr && !isLabourIndent && (
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {material.code || "-"}
                      </td>
                    )}
                    {isMaterialMaster && (
                      <td className="px-4 py-3 text-xs font-medium text-gray-700">
                        {material.materialSubType || "-"}
                      </td>
                    )}
                    <td className="px-4 py-3 font-medium text-[#1F1E29]">
                      {itemName || "-"}
                    </td>
                    <td className="px-4 py-3">{material.quantity || "-"}</td>
                    <td className="px-4 py-3">{material.unit || "-"}</td>
                    {!isPendingPr && !isLabourIndent && (
                      <td className="px-4 py-3 text-xs">
                        {material.brands?.length ? material.brands.join(", ") : "-"}
                      </td>
                    )}
                    {isPendingPr && (
                      <td className="px-4 py-3 text-xs">
                        {material.procurementModeDate || "-"}
                      </td>
                    )}
                  </tr>
                );
              })}
              {visibleMaterialIndexes.length === 0 && (
                <tr>
                  <td
                    colSpan={
                      isPendingPr
                        ? 7
                        : isLabourIndent
                          ? hasMultipleIndentTabs ? 5 : 4
                          : isMaterialMaster ? (hasMultipleIndentTabs ? 8 : 7) : (hasMultipleIndentTabs ? 7 : 6)
                    }
                    className="px-4 py-10 text-center text-sm text-gray-500"
                  >
                    No {itemLabel} match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-3 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-sm border border-gray-300 text-sm hover:bg-gray-50"
          >
            Close
          </button>
          <button
            type="button"
            onClick={onAdd}
            className="px-4 py-2 rounded-sm bg-[#1E1E08] text-white text-sm hover:bg-black"
          >
            Add Selected {isLabourIndent ? "Labour Items" : "Materials"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default IndentMaterialSelectionDrawer;
